import React, { FC, useState } from "react";

// UI components
import { Card } from "@UI/Components/Card/Card";
import { Heading } from "@UI/Components/Heading/Heading";
import { Text } from "@UI/Components/Text/Text";
import { Button } from "@UI/Components/Button/Button";
import { Box } from "@UI/Components/Box/Box";
import { Icon } from "@UI/Components/Icon/Icon";
import { LoadingScreen } from "@UI/Components/LoadingScreen/LoadingScreen";
import { EuroCircle } from "@UI/Components/Icons/EuroCircle";

// Freemium
import { useServiceFeesFeature } from "@modules/ServiceFees/hooks/useServiceFeesFeature";

import { ResponsiveFormattedMessage } from "@Shared/Components/ResponsiveFormattedMessage/ResponsiveFormattedMessage";

// i18n
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";
import messages from "./messages";

// services
import { IServiceOffer } from "@modules/ServiceAssignment/services/serviceOffers";

// routes
import {
  RouterLink,
  RouterLinkProps,
} from "@Libs/Router/Components/RouterLink/RouterLink";
import { ServiceOfferPreview } from "@modules/ServiceAssignment/Components/ServiceOfferPreview/ServiceOfferPreview";
import { Bold } from "@UI/Components/Bold/Bold";
import { ResponsiveModal } from "@UI/Components/ResponsiveModal/ResponsiveModal";

interface Props {
  serviceOffer: IServiceOffer;
  link: RouterLinkProps;
}

export const ChooseFormulaPageRedesignServiceFeeFormulaCard: FC<Props> = ({
  serviceOffer,
  link,
}) => {
  const [showPriceEstimateModal, setShowPriceEstimateModal] = useState(false);
  const { serviceFeesFeature, isLoading: isServiceFeesLoading } =
    useServiceFeesFeature();

  if (isServiceFeesLoading) {
    return <LoadingScreen />;
  }

  const togglePriceEstimateModal = () =>
    setShowPriceEstimateModal(!showPriceEstimateModal);

  const handleServiceFeeCTAClick = () => {
    togglePriceEstimateModal();
    const subscriptionCard = document.getElementById(
      "subscription-formula-card"
    );
    const toggleAdvantages = document.getElementById(
      "subscription-advantages-link"
    );
    if (toggleAdvantages) toggleAdvantages.click();

    setTimeout(() => {
      if (subscriptionCard) subscriptionCard.scrollIntoView();
    }, 300);
  };

  const serviceFeePercentage =
    serviceFeesFeature?.details?.chargedToClient?.clientServiceFee?.percentage;

  return (
    <Card
      data-testid="service-fee-formula-card"
      header={
        <Heading as="h3" paddingBottom={[2, 4]}>
          <ResponsiveFormattedMessage
            mobile={messages.serviceFeeFormula}
            desktop={messages.serviceFeeFormulaShort}
          />
        </Heading>
      }
      footer={
        <Box w="full">
          <Text mb="4">
            <Bold
              fontSize={{ base: "32px", xl: "40px" }}
              lineHeight="1"
            >{`${serviceFeePercentage}%`}</Bold>
            <Text fontSize="sm" as="span">
              <FormattedMessage {...messages.serviceFeeAppliedOnTheService} />
            </Text>
          </Text>
          <RouterLink
            as={Button}
            colorScheme="seaGreen"
            variant="outline"
            size="lg"
            width="full"
            data-testid="choose-service-fee-formula"
            {...link}
          >
            <FormattedMessage {...messages.choose} />
          </RouterLink>
        </Box>
      }
    >
      <Box marginBottom="6">
        <Text marginBottom="4">
          <FormattedMessage {...messages.getATasteOfOurServices} />
        </Text>
        <>
          <Button
            variant="link"
            colorScheme="pink"
            onClick={togglePriceEstimateModal}
            paddingX="0"
          >
            <Icon as={EuroCircle} marginRight="2" color="pink.500" />
            <FormattedMessage {...messages.viewPriceEstimate} />
          </Button>
          <ResponsiveModal
            onClose={togglePriceEstimateModal}
            isOpen={showPriceEstimateModal}
          >
            <ServiceOfferPreview
              serviceOfferId={serviceOffer?.id}
              handleServiceFeeCTAClick={handleServiceFeeCTAClick}
              showServiceFeeCTA
              showServiceFees={false}
            />
          </ResponsiveModal>
        </>
      </Box>
    </Card>
  );
};
